// Block components move to learn/components/blocks (see the learn structure)
import TextBlock from "../../learn/components/blocks/TextBlock";
import ProgramCodeBlock from "../../learn/components/blocks/ProgramCodeBlock";

import { CHEATSHEET_MESSAGES } from "../messages";
import type { CheatsheetBlock, CheatsheetContent } from "../types";

interface CheatsheetRendererProps {
    content?: CheatsheetContent | null;
}

// Only web links are allowed: a CMS value like "javascript:..." would run code when clicked
const getSafeUrl = (url?: string): string | null => {
    try {
        const parsed = new URL(url ?? "");
        return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.href : null;
    } catch {
        return null; // Not a valid absolute URL
    }
};

const renderBlock = (block: CheatsheetBlock) => {
    switch (block.type) {
        case "text":
            return <TextBlock key={block.id} config={block.config} />;

        case "code":
            return <ProgramCodeBlock key={block.id} config={block.config} />;

        case "link": {
            const href = getSafeUrl(block.config.url);
            if (!href) return null;

            return (
                <a
                    key={block.id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                    {block.config.text || block.config.url}
                </a>
            );
        }

        default:
            return null; // Block types we don't know yet are skipped
    }
};

const CheatsheetRenderer = ({ content }: CheatsheetRendererProps) => {
    if (!content || typeof content !== "object") return null;

    const blocks = Array.isArray(content.blocks) ? content.blocks : [];

    if (blocks.length === 0) {
        return <div className="py-8 text-center text-gray-500">{CHEATSHEET_MESSAGES.noContent}</div>;
    }

    return <div className="flex flex-col gap-6">{blocks.map(renderBlock)}</div>;
};

export default CheatsheetRenderer;